import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { SolicitudCompra } from '../types/solicitudCompra.types';
import { SolicitudCompraService } from '../services/solicitudCompraService';

interface SolicitudCompraState {
  items: SolicitudCompra[];
  selectedItem: SolicitudCompra | null;
  loading: boolean;
  error: string | null;

  fetchAll: () => Promise<void>;
  fetchById: (id: number) => Promise<void>;
  save: (data: SolicitudCompra) => Promise<void>;
  remove: (id: number) => Promise<void>;
  setSelectedItem: (item: SolicitudCompra | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  items: [],
  selectedItem: null,
  loading: false,
  error: null,
};

export const useSolicitudCompraStore = create<SolicitudCompraState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchAll: async () => {
        set({ loading: true, error: null }, false, 'sc/fetchAll/start');
        try {
          const items = await SolicitudCompraService.getAll();
          set({ items, loading: false }, false, 'sc/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar solicitudes' }, false, 'sc/fetchAll/error');
        }
      },

      fetchById: async (id: number) => {
        set({ loading: true, error: null }, false, 'sc/fetchById/start');
        try {
          const item = await SolicitudCompraService.getById(id);
          set({ selectedItem: item, loading: false }, false, 'sc/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar solicitud' }, false, 'sc/fetchById/error');
        }
      },

      save: async (data: SolicitudCompra) => {
        set({ loading: true, error: null }, false, 'sc/save/start');
        try {
          await SolicitudCompraService.save(data);
          set({ loading: false }, false, 'sc/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar' }, false, 'sc/save/error');
          throw error;
        }
      },

      remove: async (id: number) => {
        set({ loading: true, error: null }, false, 'sc/delete/start');
        try {
          await SolicitudCompraService.delete(id);
          set((state) => ({
            items: state.items.filter((i) => i.idSolicitudCompra !== id),
            loading: false,
          }), false, 'sc/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar' }, false, 'sc/delete/error');
        }
      },

      setSelectedItem: (item) => set({ selectedItem: item }, false, 'sc/setSelected'),
      clearError: () => set({ error: null }, false, 'sc/clearError'),
      reset: () => set(initialState, false, 'sc/reset'),
    }),
    { name: 'solicitud-compra-store' }
  )
);
